import { useEffect, useState } from "react"

type TypewriterProps = {
  roles: string[]
  // Time given to each role: typing, a short pause, then erasing.
  cycleMs?: number
}

const TYPE_SHARE = 0.55
const ERASE_SHARE = 0.3

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  )
}

export function Typewriter({ roles, cycleMs = 5000 }: TypewriterProps) {
  const [text, setText] = useState("")
  const reducedMotion = prefersReducedMotion()

  useEffect(() => {
    if (reducedMotion || roles.length === 0) return

    let timer: ReturnType<typeof setTimeout>
    let index = 0
    let length = 0
    let erasing = false

    function tick() {
      const role = roles[index]
      if (!erasing) {
        length += 1
        setText(role.slice(0, length))
        if (length === role.length) {
          erasing = true
          // The pause is whatever is left of the cycle after typing and erasing.
          timer = setTimeout(tick, cycleMs * (1 - TYPE_SHARE - ERASE_SHARE))
        } else {
          timer = setTimeout(tick, (cycleMs * TYPE_SHARE) / role.length)
        }
      } else {
        length -= 1
        setText(role.slice(0, length))
        if (length === 0) {
          erasing = false
          index = (index + 1) % roles.length
          timer = setTimeout(tick, 0)
        } else {
          timer = setTimeout(tick, (cycleMs * ERASE_SHARE) / role.length)
        }
      }
    }

    timer = setTimeout(tick, 0)
    return () => clearTimeout(timer)
  }, [roles, cycleMs, reducedMotion])

  if (reducedMotion) return <>{roles[0]}</>

  return (
    <span aria-label={roles.join(", ")}>
      <span aria-hidden="true">{text}</span>
      <span className="cursor" aria-hidden="true" />
    </span>
  )
}
